// Клієнт API аналітики й пошуку (U15) + хуки TanStack Query.
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import type {
  AnalyticsPage,
  AnalyticsReport,
  BreakAnalysis,
  DashboardView,
  GroupAnalyticsRow,
  RoomAnalyticsResponse,
  RoomHeatmap,
  SearchResponse,
  TeacherAnalyticsRow,
} from '@unisched/shared';
import { api, type QueryParams } from '@/lib/api';
import { downloadFile } from '@/lib/download';
import { todayIso } from '@/lib/format';

const enc = encodeURIComponent;

/** Вибір версії: семестр з топбару + робоча / опублікована / конкретна версія. */
export interface AnalyticsTarget {
  semesterId: string | null;
  versionId?: string | null;
  published?: boolean;
}

export function targetParams(t: AnalyticsTarget): QueryParams {
  return t.versionId ? { versionId: t.versionId } : { semesterId: t.semesterId ?? undefined, published: t.published ? true : undefined };
}

export const analyticsKeys = {
  all: ['analytics'] as const,
  dashboard: (p: QueryParams) => ['analytics', 'dashboard', p] as const,
  rooms: (p: QueryParams) => ['analytics', 'rooms', p] as const,
  heatmap: (id: string, p: QueryParams) => ['analytics', 'heatmap', id, p] as const,
  teachers: (p: QueryParams) => ['analytics', 'teachers', p] as const,
  groups: (p: QueryParams) => ['analytics', 'groups', p] as const,
  breaks: (p: QueryParams) => ['analytics', 'breaks', p] as const,
  search: (q: string) => ['search', q] as const,
};

export const analyticsApi = {
  dashboard: (p: QueryParams) => api.get<DashboardView>('/analytics/dashboard', p),
  rooms: (p: QueryParams) => api.get<RoomAnalyticsResponse>('/analytics/rooms', p),
  heatmap: (id: string, p: QueryParams) => api.get<RoomHeatmap>(`/analytics/rooms/${enc(id)}/heatmap`, p),
  teachers: (p: QueryParams) => api.get<AnalyticsPage<TeacherAnalyticsRow>>('/analytics/teachers', p),
  groups: (p: QueryParams) => api.get<AnalyticsPage<GroupAnalyticsRow>>('/analytics/groups', p),
  breaks: (p: QueryParams) => api.get<BreakAnalysis | null>('/analytics/breaks', p),
  search: (q: string, signal?: AbortSignal) => api.get<SearchResponse>('/search', { q, limit: 12, today: todayIso() }, { signal }),
  exportXlsx: (report: AnalyticsReport, p: QueryParams) =>
    downloadFile('/analytics/export.xlsx', { params: { ...p, report, page: undefined, pageSize: undefined }, fallbackName: 'Аналітика.xlsx' }),
};

const ready = (p: QueryParams) => !!(p.versionId || p.semesterId);

export function useDashboard(p: QueryParams) {
  return useQuery({ queryKey: analyticsKeys.dashboard(p), queryFn: () => analyticsApi.dashboard(p), enabled: ready(p), placeholderData: keepPreviousData });
}

export function useRoomsAnalytics(p: QueryParams) {
  return useQuery({ queryKey: analyticsKeys.rooms(p), queryFn: () => analyticsApi.rooms(p), enabled: ready(p), placeholderData: keepPreviousData });
}

export function useRoomHeatmap(id: string | null, p: QueryParams) {
  return useQuery({ queryKey: analyticsKeys.heatmap(id ?? '', p), queryFn: () => analyticsApi.heatmap(id!, p), enabled: !!id && ready(p) });
}

export function useTeachersAnalytics(p: QueryParams) {
  return useQuery({ queryKey: analyticsKeys.teachers(p), queryFn: () => analyticsApi.teachers(p), enabled: ready(p), placeholderData: keepPreviousData });
}

export function useGroupsAnalytics(p: QueryParams) {
  return useQuery({ queryKey: analyticsKeys.groups(p), queryFn: () => analyticsApi.groups(p), enabled: ready(p), placeholderData: keepPreviousData });
}

export function useBreaksAnalytics(p: QueryParams) {
  return useQuery({ queryKey: analyticsKeys.breaks(p), queryFn: () => analyticsApi.breaks(p), enabled: ready(p), placeholderData: keepPreviousData });
}

export function useSearch(q: string) {
  const text = q.trim();
  return useQuery({
    queryKey: analyticsKeys.search(text),
    queryFn: ({ signal }) => analyticsApi.search(text, signal),
    enabled: text.length > 0,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
}
